/*
 * 50-50 Large Promo mobile carousel — click-and-drag panning for fine pointers (mouse,
 * trackpad) that lack their own way to move the track. A touchscreen finger already
 * scrolls it natively (native swipe), so a desktop browser resized to the carousel
 * breakpoint has no way to reach the second panel without this. Vertical mouse-wheel
 * scrolling over the track is left completely untouched — it must keep scrolling the
 * page as normal.
 *
 * Gated on contact size (`width`/`height`), not `pointerType`: depending on the OS/
 * browser, a laptop trackpad's click-drag can report `pointerType` as `mouse` or as
 * `touch`, but its contact size is always ~1px either way (a precise point, not a
 * finger). A genuine touchscreen finger reports a real contact area (>1px). Checking
 * size instead of the type label means a trackpad is never mistaken for a finger.
 *
 * Hooks: [data-large-promo-track].
 */

class LargePromoCarousel {
  constructor(track) {
    this.track = track;
    this.suppressClick = false;
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onClick = this.onClick.bind(this);
    track.addEventListener('pointerdown', this.onPointerDown);
    // Capture phase, so a drag's release click is stopped here before it reaches the
    // panel's link.
    track.addEventListener('click', this.onClick, true);
  }

  onClick(e) {
    if (!this.suppressClick) return;
    this.suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  }

  onPointerDown(e) {
    // A real touchscreen finger reports a genuine contact area; a mouse, trackpad,
    // or pen always reports ~1px regardless of what pointerType label it gets.
    if ((e.width || 0) > 1 || (e.height || 0) > 1) return;
    // Let the play/pause and mute/unmute buttons handle their own clicks — never
    // hijack a click on them into a drag gesture.
    if (e.target.closest('button')) return;

    const track = this.track;
    if (track.scrollWidth <= track.clientWidth) return;

    // Suppresses the browser's native "drag this link/image" ghost so our own
    // pan takes over instead.
    e.preventDefault();

    const startX = e.clientX;
    const startScrollLeft = track.scrollLeft;
    const pointerId = e.pointerId;
    let dragging = false;

    const onMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 4) return;
        dragging = true;
        // Capture keeps the pan following the pointer once it leaves the track.
        // It is not relied on to stop the release click: Chromium retargets that
        // click to the track, but Safari still fires it on the link under the
        // pointer, so onUp flags it for onClick to cancel instead.
        try { track.setPointerCapture(pointerId); } catch (_) {}
        track.style.cursor = 'grabbing';
      }
      track.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onUp);
      if (dragging) {
        try { track.releasePointerCapture(pointerId); } catch (_) {}
        track.style.cursor = '';
        // The release click follows in the same task as pointerup, so clearing the
        // flag on the next task means a release that fires no click (outside the
        // window) can never swallow a later, genuine click.
        this.suppressClick = true;
        setTimeout(() => { this.suppressClick = false; }, 0);
      }
    };

    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
  }

  destroy() {
    this.track.removeEventListener('pointerdown', this.onPointerDown);
    this.track.removeEventListener('click', this.onClick, true);
  }
}

function initTracks(container = document) {
  const selector = '[data-large-promo-track]:not([data-promos-carousel-init])';
  const tracks = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  tracks.forEach((track) => {
    track.dataset.promosCarouselInit = 'true';
    track._carouselInstance = new LargePromoCarousel(track);
  });
}

initTracks();
document.addEventListener('shopify:section:load', (event) => initTracks(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const track = event.target.querySelector?.('[data-large-promo-track]') || event.target;
  if (track?._carouselInstance) {
    track._carouselInstance.destroy();
    delete track._carouselInstance;
  }
});
